
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { SingleDocumentUpload } from './SingleDocumentUpload';
import { OtherDocumentsUpload } from './OtherDocumentsUpload';
import { DocumentUploadProps, OtherDocument, MAX_OTHER_DOCUMENTS, STRING_FIELDS, ARRAY_FIELDS } from './types';
import { handleDocumentUpload } from './utils';
import type { Application } from '@/types/database.types';

export function DocumentManager({ data, onSave }: DocumentUploadProps) {
  const { toast } = useToast();
  const { reset } = useForm<Partial<Application>>();
  const [otherDocuments, setOtherDocuments] = useState<OtherDocument[]>([]);

  useEffect(() => {
    reset(data);

    // Initialize otherDocuments from the data
    if (data.other_documents) {
      try {
        if (typeof data.other_documents === 'string') {
          setOtherDocuments(JSON.parse(data.other_documents));
        } else if (Array.isArray(data.other_documents)) {
          // If other_documents is already an array, we need to convert it to our format
          const docs = data.other_documents.map((url, index) => ({
            name: `Document ${index + 1}`,
            url
          }));
          setOtherDocuments(docs);
        }
      } catch (e) {
        console.error('Error parsing other_documents', e);
        setOtherDocuments([]);
      }
    }
  }, [data, reset]);

  const handleFileUpload = async (file: File, field: string) => {
    try {
      toast({
        title: 'Uploading file...',
        description: `Uploading ${file.name}`,
      });
      
      await handleDocumentUpload(
        file, 
        field, 
        data, 
        otherDocuments, 
        onSave, 
        setOtherDocuments, 
        MAX_OTHER_DOCUMENTS
      );

      toast({
        title: 'File uploaded',
        description: 'Your document has been uploaded successfully.',
      });
    } catch (error: any) {
      console.error('File upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred during upload',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveOtherDocument = async (index: number) => {
    const newOtherDocuments = otherDocuments.filter((_, i) => i !== index);
    setOtherDocuments(newOtherDocuments);
    
    // Use a safe type conversion
    await onSave({
      other_documents: JSON.stringify(newOtherDocuments) as unknown as string[]
    });
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-4">
          <SingleDocumentUpload
            id="employee_census"
            label="Employee Census"
            accept=".csv,.xlsx,.pdf"
            currentFile={data.employee_census_file}
            onUpload={(file) => handleFileUpload(file, 'employee_census_file')}
          />

          <SingleDocumentUpload
            id="dependent_census"
            label="Dependent Census"
            accept=".csv,.xlsx,.pdf"
            currentFile={data.dependent_census_file}
            onUpload={(file) => handleFileUpload(file, 'dependent_census_file')}
          />

          <SingleDocumentUpload
            id="workers_comp_declaration"
            label="Workers Comp Declaration"
            accept=".pdf"
            currentFile={data.workers_comp_declaration_file}
            onUpload={(file) => handleFileUpload(file, 'workers_comp_declaration_file')}
          />

          <SingleDocumentUpload
            id="workers_comp_loss_runs"
            label="Workers Comp Loss Runs"
            accept=".pdf"
            currentFile={data.workers_comp_loss_runs_file}
            onUpload={(file) => handleFileUpload(file, 'workers_comp_loss_runs_file')}
          />

          <SingleDocumentUpload
            id="health_insurance_renewal"
            label="Health Insurance Renewal"
            accept=".pdf"
            currentFile={data.health_insurance_renewal_file}
            onUpload={(file) => handleFileUpload(file, 'health_insurance_renewal_file')}
          />

          <SingleDocumentUpload
            id="tax_exempt_status"
            label="Proof of Tax Exempt Status"
            accept=".pdf"
            currentFile={data.tax_exempt_status_file}
            onUpload={(file) => handleFileUpload(file, 'tax_exempt_status_file')}
          />

          <OtherDocumentsUpload
            documents={otherDocuments}
            maxDocuments={MAX_OTHER_DOCUMENTS}
            onUpload={(file) => handleFileUpload(file, 'other')}
            onRemove={handleRemoveOtherDocument}
          />
        </div>
      </CardContent>
    </Card>
  );
}
